import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useLangContext } from "../../../../context/langContext";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import { ReactNode, useEffect, useState } from "react";
import { dateFormatter } from "../../../../utils/date-formatter";

import { useNotificationContext } from "../../../../context/notificationContext";
import errorResolver from "../../../../utils/error-resolver";
import ReloadButton from "../../../common/reload-button/ReloadButton";
import { Link, useNavigate } from "react-router-dom";
import {
  PbxServerListColumnFilterable,
  PbxServersListRequest,
} from "../../../../services/Endpoints/pbxserver/PbxServerListEndpoint";
import { FilterOperatorEnum } from "../../../../services/Schemas/ListQueryString";
import { PbxServerSchema } from "../../../../services/Schemas/PbxServer";
import PbxTrunkTabsComponent from "./PbxTrunkTable";
import PbxServerMedSectionComponent from "./PbxServerMedSec";
import InfoBadge, { OriginEnum } from "../../../common/info-badge/InfoBadge";

interface IBroadcastDetailsBoardProps {
  id: string;
}

const infos = {
  en: {
    title: "عنوان کمپین",
    ticketNumber: "کد پیگیری",
    created_at: "تاریخ ایجاد",
  },
  fa: {
    title: "نام کاربری",
    ticketNumber: "لینک سرور",
    created_at: "تاریخ ایجاد",
  },
};

interface PbxServerData extends PbxServerSchema {
  _status: ReactNode;
}

export default function PbxServerDetailsSectionComponent({
  id,
}: Readonly<IBroadcastDetailsBoardProps>) {
  const { dir, lang } = useLangContext();
  const [data, setData] = useState<PbxServerData[]>([]);
  const [waiting, setWaiting] = useState(false);
  const protectedInstance = useProtectedAxiosInstance();
  const [reload, setReload] = useState(false);
  const navigate = useNavigate();

  const { setNotification } = useNotificationContext();

  const getData = async () => {
    try {
      setWaiting(true);
      const broadcastingDetailsRequest = new PbxServersListRequest(
        protectedInstance
      );
      const { data } = await broadcastingDetailsRequest.send({
        filters: [
          {
            column: PbxServerListColumnFilterable.ID,
            operator: FilterOperatorEnum.EQUAL,
            value: id,
          },
        ],
      });
      setData(
        data.pbx_servers.map((server) => ({
          ...server,
          _status: (
            <InfoBadge
              origin={OriginEnum.SERVER}
              status={(server?.status ?? "online") as "online"}
            />
          ),
        }))
      );
      setWaiting(false);
    } catch (error: any) {
      errorResolver({ error, setNotification });
      navigate("/404");
      console.error(error.message);
    }
  };

  useEffect(() => {
    getData();
  }, [reload]);

  return data.length > 0 ? (
    <Box
      sx={{
        width: "100%",
        typography: "body1",
        backgroundColor: "white",
        borderRadius: "6px",
        minHeight: "70vh",
        position: "relative",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          width: "100%",
          px: "18px",
          py: "16px",
        }}
      >
        <Stack direction="column" alignItems="start" gap="4px">
          <Typography
            variant="h6Regular"
            color={(theme) => theme.palette.dominant[400]}
            sx={{
              display: "flex",
              gap: "5px",
            }}
          >
            {infos[lang].title}:
            <Typography variant="h6Regular">{data[0].username}</Typography>
          </Typography>
          <Typography
            variant="h6Regular"
            color={(theme) => theme.palette.dominant[400]}
            sx={{
              display: "flex",
              gap: "5px",
            }}
          >
            {infos[lang].ticketNumber}:
            <Typography
              variant="h6Regular"
              color={(theme) => theme.palette.others.link}
              component={Link}
              sx={{
                textDecoration: "none",
              }}
              to={data[0].server_url}
            >
              {data[0].server_url}
            </Typography>
          </Typography>
        </Stack>
        <Stack direction="column" alignItems="end" gap="4px">
          <Stack direction="row" gap="4px">
            {data[0]._status ?? ""}
            <ReloadButton size="sm" reloadAction={() => setReload(!reload)} />
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            width="100%"
            gap="8px"
          >
            <Typography
              variant="labelRegular"
              color={(theme) => theme.palette.dominant[400]}
            >
              {infos[lang].created_at}:
            </Typography>
            <Typography
              variant="labelRegular"
              color={(theme) => theme.palette.dominant[400]}
            >
              {dateFormatter({ lang, date: data[0].created_at })}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {waiting && <LinearProgress />}
      <Stack mx="18px" my="10px" gap="28px" maxHeight={"65vh"}>
        <PbxServerMedSectionComponent pbxServerInfo={data[0]} />
        <PbxTrunkTabsComponent
          id={id}
          handleReload={() => setReload(!reload)}
          reload={reload}
        />
      </Stack>
    </Box>
  ) : (
    <Box>
      <LinearProgress />
    </Box>
  );
}
